import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { LiquidLogo } from "@/components/ui/liquid-logo";
import { resolveApiUrl } from "@/providers/api-url";

const API_URL = resolveApiUrl();

interface InviteValidationResponse {
  success: boolean;
  data?: { email: string };
  error?: string;
}

interface SetPasswordResponse {
  success: boolean;
  error?: string;
}

type PageState =
  | { status: "loading" }
  | { status: "invalid"; message: string }
  | { status: "valid"; email: string }
  | { status: "success" };

/**
 * Публичная страница установки пароля по токену приглашения.
 *
 * Flow:
 * 1. Админ получает email со ссылкой вида /set-password?token=<hex>
 * 2. Страница валидирует токен через GET /auth/admin/invite/:token
 * 3. Если токен валиден — показывает форму для ввода пароля
 * 4. POST /auth/admin/set-password устанавливает пароль
 * 5. После успеха — редирект на /login
 */
export function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const { theme, resolvedTheme } = useTheme();

  const [state, setState] = useState<PageState>({ status: "loading" });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentTheme = resolvedTheme || theme || "dark";
  const logoSrc =
    currentTheme === "dark" ? "/logo-white.svg" : "/logo-black.svg";

  useEffect(() => {
    if (!token) {
      setState({ status: "invalid", message: "Ссылка недействительна: отсутствует токен" });
      return;
    }

    let cancelled = false;

    const validate = async () => {
      try {
        const response = await fetch(
          `${API_URL}/auth/admin/invite/${encodeURIComponent(token)}`,
          { method: "GET", headers: { "Content-Type": "application/json" } },
        );
        const json: InviteValidationResponse = await response.json();

        if (cancelled) {
          return;
        }

        if (!response.ok || !json.success || !json.data) {
          setState({
            status: "invalid",
            message: json.error ?? "Приглашение недействительно или истекло",
          });
          return;
        }

        setState({ status: "valid", email: json.data.email });
      } catch {
        if (!cancelled) {
          setState({
            status: "invalid",
            message: "Не удалось проверить приглашение. Попробуйте позже.",
          });
        }
      }
    };

    void validate();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (password.length < 6) {
      setSubmitError("Пароль должен быть не менее 6 символов");
      return;
    }

    if (password !== confirmPassword) {
      setSubmitError("Пароли не совпадают");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/auth/admin/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const json: SetPasswordResponse = await response.json();

      if (!response.ok || !json.success) {
        setSubmitError(json.error ?? "Не удалось установить пароль");
        return;
      }

      setState({ status: "success" });
    } catch {
      setSubmitError("Ошибка сети. Попробуйте ещё раз.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (state.status === "success") {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4 overflow-hidden">
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none select-none">
        <LiquidLogo
          height="50vh"
          opacity={currentTheme === "dark" ? 0.15 : 0.1}
        />
      </div>

      <Card className="relative z-10 w-full max-w-md shadow-2xl bg-card border-primary/10">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto flex items-center justify-center h-16">
            <img src={logoSrc} alt="Masters Club" className="h-full w-auto" />
          </div>
          <h1 className="mt-4 text-lg font-semibold">Установка пароля</h1>
        </CardHeader>
        <CardContent>
          {state.status === "loading" && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {state.status === "invalid" && (
            <div className="space-y-4">
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                {state.message}
              </div>
              <Button asChild variant="secondary" className="w-full">
                <Link to="/login">Перейти ко входу</Link>
              </Button>
            </div>
          )}

          {state.status === "valid" && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Задайте пароль для аккаунта{" "}
                <span className="font-medium text-foreground">{state.email}</span>.
              </p>

              <div className="space-y-2">
                <Label htmlFor="password">Новый пароль</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Повторите пароль</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>

              {submitError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                  {submitError}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Сохранение...
                  </>
                ) : (
                  "Установить пароль"
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
